import { useCallback, useEffect, useState } from "react";

import { type VirtualTableSortEvent, usePrevious } from "@snapchat/snap-design-system";
import { SortOrder } from "@snapchat/snap-design-system/lib/components/VirtualTable/customPlugins/useSdsSortBy";

import { FilterService } from "../domain/service";

interface UseTableFilterAndSort<T> {
    columnId: string;
    sortOrder: SortOrder;
    data: readonly T[];
    onColumnSort: (event: VirtualTableSortEvent) => void;
}

interface UseTableFilterAndSortProps<T> {
    tableData: readonly T[];
    initialColumn?: string;
    initialSortOrder?: SortOrder;
    searchText?: string;
    getSearchKey?: (value: T) => string;
    initialRemoveFromSortKey?: string;
}

function useTableFilterAndSort<T extends Record<string, unknown>>({
    tableData,
    initialColumn = "",
    initialSortOrder = SortOrder.Desc,
    searchText,
    getSearchKey,
    initialRemoveFromSortKey,
}: UseTableFilterAndSortProps<T>): UseTableFilterAndSort<T> {
    const [tableState, setTableState] = useState({
        data: tableData,
        sortOrder: initialSortOrder,
        columnId: initialColumn,
    });
    const [isSorted, setIsSorted] = useState(false);
    const prev = usePrevious(searchText);

    const prevTableData = usePrevious(tableData);

    const handleSort = useCallback(
        (dataToSort: readonly T[], tableColId?: string, tableSortOrder?: SortOrder, isFirstSort?: boolean) => {
            const { columnId, sortOrder } = tableState;

            return [...dataToSort].sort((a, b) => {
                let compareA = a[tableColId ?? columnId];
                let compareB = b[tableColId ?? columnId];

                // Only over ride if user has clicked on a column to sort by
                if (!isFirstSort && !isSorted && initialRemoveFromSortKey && b[initialRemoveFromSortKey]) {
                    return 1;
                }

                if (typeof compareA === "string" && typeof compareB === "string") {
                    compareA = compareA.toLowerCase();
                    compareB = compareB.toLowerCase();
                }

                const direction = (tableSortOrder ?? sortOrder) === SortOrder.Desc ? 1 : -1;

                return (compareA as any) > (compareB as any) ? -direction : direction;
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isSorted, tableState],
    );

    const onTableFilter = useCallback(() => {
        const filter = new FilterService(searchText as string);
        const filtered = filter.applyFilter(tableData, getSearchKey || (() => ""));

        const sorted = handleSort(filtered);
        setTableState((oldTableState) => ({
            ...oldTableState,
            data: sorted,
        }));
    }, [getSearchKey, handleSort, searchText, tableData]);

    const onColumnSort = useCallback(
        ({ columnId, sortOrder }: VirtualTableSortEvent) => {
            const sortData = handleSort(tableState.data, columnId, sortOrder, true);
            setTableState({
                data: sortData,
                sortOrder,
                columnId,
            });
            setIsSorted(true);
        },
        [handleSort, tableState.data],
    );

    useEffect(() => {
        // re-trigger search if table data changes
        if (JSON.stringify(prevTableData) === JSON.stringify(tableData)) {
            return;
        }
        if (searchText && getSearchKey) {
            onTableFilter();
        } else {
            setTableState((oldTableState) => ({ ...oldTableState, data: tableData }));
        }
    }, [getSearchKey, onTableFilter, prevTableData, searchText, tableData]);

    useEffect(() => {
        // only trigger filter if search value is new
        if (prev !== searchText && getSearchKey) {
            onTableFilter();
        }
    }, [getSearchKey, onTableFilter, prev, searchText]);

    return {
        ...tableState,
        onColumnSort,
    };
}

export { useTableFilterAndSort };
